import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';
import { isKey, Key } from '@app/shared/key';
import { AuthService } from '@app/shared/services/auth.service';
import { ProfileSkillsListService } from '@app/profile/services/profile-skills-list.service';

@Component({
  selector: 'dgx-profile-skills-list-self-rating',
  templateUrl: './profile-skills-list-self-rating.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSkillsListSelfRatingComponent implements OnInit {
  @Input() public tag: TagsApi.TagDetails;
  @Input() public isOwner: boolean;
  @Input() public showLock: boolean;
  @Output() public tagRatingChange = new EventEmitter<any>();

  public i18n: { [key: string]: string } = {};

  constructor(
    private tagRatingService: TagRatingService,
    private tagsService: TagsService,
    private translateService: TranslateService,
    private authService: AuthService,
    private profileSkillsListService: ProfileSkillsListService
  ) {}

  public get rating(): TagsApi.UserTagRating {
    return this.tag.ratings?.find(
      (rating) => rating.type === InternalTagRatingTypes.self
    );
  }
  public get level(): string | undefined {
    return this.rating?.level;
  }

  public get showDonut(): boolean {
    // We need to check the privacyID here as private ratings for non-owners are being returned from the BE
    // The long term plan is to fix the BE end point so that these are not returned. From PD-69894
    return this.isOwner || (this.rating?.privacyId > 0 && !!this.level);
  }

  public get showDefaultPlus(): boolean {
    return !this.level && this.isOwner;
  }

  public get tabIndex(): 0 | undefined {
    return this.isOwner ? 0 : undefined;
  }

  public get role(): 'button' | undefined {
    return this.isOwner ? 'button' : undefined;
  }

  public get dgat(): string | undefined {
    return this.isOwner
      ? `profile-skills-list-self-cta-${this.tag.name}`
      : undefined;
  }

  private get levelText(): string {
    return this.profileSkillsListService.getLevelText(this.level);
  }

  private get baseText(): string {
    return this.level
      ? this.i18n.dgTagRating_SelfAssessment
      : this.i18n.dgTagRating_AddSelfAssessment;
  }

  private get privacyText(): string {
    return this.profileSkillsListService.getPrivacyText(
      this.level,
      this.isOwner,
      this.showLock,
      this.authService.authUser?.participateInTeamProgram ?? false
    );
  }

  public get ariaLabel(): string {
    let label = '';
    if (this.level) {
      label = this.translateService.instant(
        'dgTagRating_A11yChangeSelfRating',
        {
          skillName: this.tag.title,
          levelNumber: this.level,
        }
      );
    } else {
      label = this.translateService.instant('dgTagRating_A11yAddSelfRating', {
        skillName: this.tag.title,
      });
    }
    label = this.profileSkillsListService.getAriaLabelText(
      label,
      this.privacyText,
      ''
    );
    return label;
  }

  public get tooltip(): string {
    return this.profileSkillsListService.getTooltipText(
      this.baseText,
      this.privacyText
    );
  }

  public ngOnInit(): void {
    this.i18n = this.translateService.instant(
      [
        'dgTagRating_SelfAssessment',
        'dgTagRating_RatingTitleFormat',
        'dgTagRating_AddSelfAssessment',
        'Core_Private',
        'Core_LimitedVisibility',
      ],
      {
        tagName: this.tag.title,
      }
    );
  }

  public clickHandler(event: MouseEvent): void {
    this.eventHandler(event);
  }

  public keydownHandler(event: KeyboardEvent): void {
    if (isKey(event, Key.Enter, Key.Space)) {
      // prevent spacebar from scrolling screen
      event.preventDefault();
      this.eventHandler(event);
    }
  }

  private eventHandler(event: Event): void {
    if (this.isOwner) {
      // prevent event from triggering skill modal
      event.stopPropagation();
      this.tagRatingService
        .openSelfRatingModal(event, {
          ...this.tag,
          rating: { level: this.level } as TagsApi.UserTagRating,
        })
        .subscribe((rating: Partial<TagsApi.UserTagRatingDetails>) => {
          this.tagRatingChange.emit({
            name: this.tag.name,
            level: rating.level,
          });
        });
    }
  }

  public getLevelDescription(level: string): string {
    return this.tagsService.orgLevels.find((l) => l.value.toString() === level)
      ?.description;
  }
}
